import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Product {
  id: string
  sku: string
  name: string
  category: string
  total_stock: number
  available_stock: number
  reorder_point: number
  unit_cost: number
  status: string
}

interface Warehouse {
  id: string
  name: string
  location: string
  region: string
  capacity_units: number
  used_units: number
  status: string
}

interface Alert {
  id: string
  type: string
  severity: string
  product_id: string
  warehouse_id: string
  message: string
  acknowledged: boolean
  created_at: string
}

export default function InventoryManagementPage() {
  const [products, setProducts] = useState<Product[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [productsRes, warehousesRes, alertsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/inventory/products'),
      fetch(API_BASE + '/inventory/warehouses'),
      fetch(API_BASE + '/inventory/alerts'),
      fetch(API_BASE + '/inventory/statistics')
    ])
    if (productsRes.ok) setProducts((await productsRes.json()).products || [])
    if (warehousesRes.ok) setWarehouses((await warehousesRes.json()).warehouses || [])
    if (alertsRes.ok) setAlerts((await alertsRes.json()).alerts || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in_stock': return 'bg-green-100 text-green-800'
      case 'low_stock': return 'bg-yellow-100 text-yellow-800'
      case 'out_of_stock': return 'bg-red-100 text-red-800'
      case 'active': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200'
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Inventory Management</h1>
          <div className="flex gap-2">
            <button className="px-4 py-2 bg-white border rounded-lg hover:bg-gray-50">Import</button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Product</button>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Products</div>
              <div className="text-2xl font-bold">{statistics.total_products}</div>
              <div className="text-xs text-green-600">{statistics.in_stock} in stock</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Low Stock</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.low_stock}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Out of Stock</div>
              <div className="text-2xl font-bold text-red-600">{statistics.out_of_stock}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Inventory Value</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_inventory_value)}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-5 gap-4 mb-8">
          {warehouses.map(wh => (
            <div key={wh.id} className="bg-white rounded-lg shadow p-4">
              <div className="font-semibold mb-1">{wh.name}</div>
              <div className="text-sm text-gray-500 mb-2">{wh.location}</div>
              <div className="mb-2">
                <div className="flex justify-between text-xs mb-1">
                  <span>Capacity</span>
                  <span>{Math.round((wh.used_units / wh.capacity_units) * 100)}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${(wh.used_units / wh.capacity_units) > 0.9 ? 'bg-red-500' : (wh.used_units / wh.capacity_units) > 0.7 ? 'bg-yellow-500' : 'bg-green-500'}`}
                       style={{ width: `${(wh.used_units / wh.capacity_units) * 100}%` }}></div>
                </div>
              </div>
              <div className="text-xs text-gray-500">{wh.used_units.toLocaleString()} / {wh.capacity_units.toLocaleString()} units</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Products</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {products.slice(0, 10).map(product => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{product.name}</div>
                      <div className="text-sm text-gray-500 font-mono">{product.sku}</div>
                    </td>
                    <td className="px-6 py-4 text-sm">{product.category}</td>
                    <td className="px-6 py-4">
                      <div className="font-medium">{product.available_stock} / {product.total_stock}</div>
                      <div className="text-xs text-gray-500">Reorder at {product.reorder_point}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(product.status)}`}>
                        {product.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Alerts ({alerts.filter(a => !a.acknowledged).length})</h3>
            <div className="space-y-3">
              {alerts.filter(a => !a.acknowledged).slice(0, 8).map(alert => (
                <div key={alert.id} className={`p-3 rounded border ${getSeverityColor(alert.severity)}`}>
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm capitalize">{alert.type.replace(/_/g, ' ')}</span>
                    <span className="text-xs">{alert.severity}</span>
                  </div>
                  <div className="text-xs">{alert.message}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
